import { DATA_TYPES } from './catalog';
import { getOffer } from './fixtures/sourceCatalog';
import {
  DataSource, DataTypeKey, IntegrationPoint, IntegrationPointStatus,
  PointSummary, SourceSummary,
} from './types';

/**
 * One point's status. Order matters: an explicit answer beats an absent one, and the
 * source's own health beats the point's mapping state.
 */
// `sourceError` is unreachable in Phase 1: nothing in the demo store ever sets a source's status to
// 'error'. It is derived here anyway because Phase 2 sets it from a failed run, and deriving it now
// keeps the status logic in one place. Do not add a demo control to fake it.
export function statusForPoint(point: IntegrationPoint, sources: DataSource[]): IntegrationPointStatus {
  if (point.owner === 'notTracked') return 'notTracked';
  if (point.owner === 'mowazaf') return 'native';
  if (point.owner === 'unset' || !point.sourceId) return 'notSet';

  const source = sources.find((s) => s.id === point.sourceId);
  // A dangling FK is not a state the UI can explain, so it reads as undecided.
  if (!source) return 'notSet';
  if (source.status === 'error') return 'sourceError';
  if (!getOffer(source.system, point.dataType)) return 'notOffered';
  return 'connected';
}

export interface PointView {
  point: IntegrationPoint;
  status: IntegrationPointStatus;
  /** Resolved display name of the chosen source, or undefined when none is chosen. */
  sourceName?: string;
}

export function buildPointViews(
  points: IntegrationPoint[], sources: DataSource[],
): PointView[] {
  return DATA_TYPES.map((def) => {
    const point = points.find((p) => p.dataType === def.key)
      ?? { dataType: def.key, module: def.module, owner: 'unset', sourceId: null, mapping: [], enriched: false };
    return {
      point,
      status: statusForPoint(point, sources),
      sourceName: sources.find((s) => s.id === point.sourceId)?.displayName,
    };
  });
}

export function summarisePoints(views: PointView[]): PointSummary {
  return {
    mapped: views.filter((v) => v.status === 'connected' || v.status === 'native').length,
    notSet: views.filter((v) => v.status === 'notSet').length,
    notTracked: views.filter((v) => v.status === 'notTracked').length,
    total: views.length,
  };
}

export function summariseSources(sources: DataSource[]): SourceSummary {
  return {
    connected: sources.filter((s) => s.status === 'connected').length,
    errored: sources.filter((s) => s.status === 'error').length,
    total: sources.length,
  };
}

/** Sources a point may choose: every registered source that is not errored. */
export function selectableSources(sources: DataSource[]): DataSource[] {
  return sources.filter((s) => s.status === 'connected');
}

/** True when no point references this source, which is the condition for allowing removal. */
export function isSourceRemovable(sourceId: string, points: IntegrationPoint[], sources: DataSource[]): boolean {
  const source = sources.find((s) => s.id === sourceId);
  if (!source) return false;
  return !points.some((p) => p.sourceId === sourceId);
}

/** Data types still pointing at a source, used to explain why removal is blocked. */
export function pointsUsingSource(sourceId: string, points: IntegrationPoint[]): DataTypeKey[] {
  return points.filter((p) => p.sourceId === sourceId).map((p) => p.dataType);
}

/** The most recent successful run timestamp across every point, for the page header. */
export function lastActivityOn(points: IntegrationPoint[]): string | undefined {
  return points
    .map((p) => p.lastUpdatedOn)
    .filter((d): d is string => Boolean(d))
    .sort()
    .reverse()[0];
}
